(() => {
  const TRANSITION_MS = 400;
  const BUFFER_SETS = 2;

  const initPoweredCarousel = (root) => {
    const track = root.querySelector('[data-pilates-powered-track]');
    const viewport = root.querySelector('[data-pilates-powered-viewport]');
    const carousel = root.querySelector('.pilates-powered__carousel');
    const prevBtn = root.querySelector('[data-pilates-powered-prev]');
    const nextBtn = root.querySelector('[data-pilates-powered-next]');

    if (!track || !viewport) return;

    const origSlides = Array.from(track.querySelectorAll('[data-pilates-powered-slide]'));
    if (origSlides.length === 0) return;

    const origCount = origSlides.length;
    let headRow = 0; // virtual row index of the first set in DOM
    let tailRow = 0; // virtual row index of the last set in DOM

    const makeSet = () => {
      const frag = document.createDocumentFragment();
      origSlides.forEach((slide) => {
        const clone = slide.cloneNode(true);
        clone.removeAttribute('data-pilates-powered-slide');
        clone.setAttribute('aria-hidden', 'true');
        clone.dataset.pilatesPoweredClone = 'true';
        clone.classList.remove('is-active');
        frag.appendChild(clone);
      });
      return frag;
    };

    // Prepend BUFFER_SETS before originals
    for (let i = 0; i < BUFFER_SETS; i++) {
      headRow--;
      track.insertBefore(makeSet(), track.firstChild);
    }
    // Append BUFFER_SETS after originals
    for (let i = 0; i < BUFFER_SETS; i++) {
      tailRow++;
      track.appendChild(makeSet());
    }

    const parsedDefault = parseInt(carousel?.dataset.defaultActiveIndex ?? '', 10);
    const defaultOffset =
      Number.isFinite(parsedDefault) && parsedDefault >= 0 && parsedDefault < origCount
        ? parsedDefault
        : Math.floor((origCount - 1) / 2);

    let activeIndex = BUFFER_SETS * origCount + defaultOffset;
    let isTransitioning = false;

    // Live HTMLCollection — no Array.from on every call
    const slides = track.children;

    // Only touch the two affected slides instead of iterating all
    const setActiveClass = (newIdx, oldIdx) => {
      if (oldIdx >= 0 && oldIdx < slides.length) slides[oldIdx].classList.remove('is-active');
      if (newIdx >= 0 && newIdx < slides.length) slides[newIdx].classList.add('is-active');
    };

    const getTargetX = () => {
      const slide = slides[activeIndex];
      if (!slide) return 0;
      const isMobile = window.matchMedia('(max-width: 1024px)').matches;
      if (isMobile) {
        const edgePad = parseFloat(getComputedStyle(viewport).paddingInlineStart) || 0;
        // Use rendered coordinates so the result is independent of DOM nesting / transforms.
        // We want: slide's visual left = viewport's visual left + edgePad
        const mat = new DOMMatrix(getComputedStyle(track).transform);
        const currentX = mat.m41;
        const slideLeft   = slide.getBoundingClientRect().left;
        const viewportLeft = viewport.getBoundingClientRect().left;
        return currentX + (viewportLeft + edgePad - slideLeft);
      }
      return viewport.clientWidth / 2 - (slide.offsetLeft + slide.offsetWidth / 2);
    };

    const applyPosition = ({ animate = true } = {}) => {
      const x = getTargetX();
      if (!animate) {
        track.classList.add('no-transition');
        track.getBoundingClientRect();
        track.style.transform = `translateX(${x}px)`;
        track.getBoundingClientRect();
        track.classList.remove('no-transition');
      } else {
        track.style.transform = `translateX(${x}px)`;
      }
    };

    // Called once after each transition — adds/removes exactly one set per side if needed
    const ensureBuffer = () => {
      const total = slides.length;

      // --- forward buffer ---
      if (total - 1 - activeIndex < BUFFER_SETS * origCount) {
        tailRow++;
        track.appendChild(makeSet());
      }

      // --- backward buffer ---
      if (activeIndex < BUFFER_SETS * origCount) {
        headRow--;
        track.insertBefore(makeSet(), track.firstChild);
        activeIndex += origCount;
        applyPosition({ animate: false });
      }

      // --- prune tail (one set at a time, only if well beyond KEEP threshold) ---
      const KEEP = BUFFER_SETS + 2;
      if (slides.length - 1 - activeIndex > KEEP * origCount) {
        for (let i = 0; i < origCount; i++) track.removeChild(track.lastChild);
        tailRow--;
      }

      // --- prune head ---
      if (activeIndex > KEEP * origCount) {
        for (let i = 0; i < origCount; i++) track.removeChild(track.firstChild);
        activeIndex -= origCount;
        headRow++;
        applyPosition({ animate: false });
      }
    };

    const setActive = (index) => {
      if (isTransitioning) return;
      isTransitioning = true;

      const oldIndex = activeIndex;
      activeIndex = index;
      setActiveClass(activeIndex, oldIndex);

      window.requestAnimationFrame(() => {
        applyPosition({ animate: true });
        window.setTimeout(() => {
          isTransitioning = false;
          ensureBuffer();
        }, TRANSITION_MS + 50);
      });
    };

    // ── autoplay ─────────────────────────────────────────────────────────────
    const autoplayEnabled = carousel?.dataset.autoplay !== 'false';
    const autoplayInterval = parseInt(carousel?.dataset.autoplayInterval ?? '4000', 10);
    let autoplayTimer = null;

    const startAutoplay = () => {
      if (!autoplayEnabled) return;
      stopAutoplay();
      autoplayTimer = window.setInterval(() => {
        if (!isTransitioning) setActive(activeIndex + 1);
      }, autoplayInterval);
    };

    const stopAutoplay = () => {
      if (autoplayTimer) { window.clearInterval(autoplayTimer); autoplayTimer = null; }
    };

    prevBtn?.addEventListener('click', () => { stopAutoplay(); setActive(activeIndex - 1); startAutoplay(); });
    nextBtn?.addEventListener('click', () => { stopAutoplay(); setActive(activeIndex + 1); startAutoplay(); });

    viewport.addEventListener('mouseenter', stopAutoplay);
    viewport.addEventListener('mouseleave', startAutoplay);

    let resizeTimer;
    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => applyPosition({ animate: false }), 150);
    });

    slides[activeIndex]?.classList.add('is-active');
    applyPosition({ animate: false });
    startAutoplay();
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-pilates-powered]').forEach(initPoweredCarousel);
  };

  initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
